import styled from 'styled-components';
import { COLORS, pickThemeFontStyles } from '@/theme';
import { Button } from '@/ui';

export const StyledModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-height: 75vh;
`;

export const StyledTitle = styled.h2`
  ${pickThemeFontStyles('24', '30', 'NORMAL')};
  margin: 0;
  color: ${COLORS.BLACK};
`;

export const StyledSubtitle = styled.p`
  ${pickThemeFontStyles('14', '22', 'NORMAL')};
  margin: 0;
  color: ${COLORS.CHARCOAL};
`;

export const StyledSearchRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  column-gap: 0.75rem;
  row-gap: 0.5rem;
  align-items: start;
`;

export const StyledSearchFieldWrap = styled.div`
  position: relative;
  grid-column: 1;
  grid-row: 2;
  min-width: 0;
`;

export const StyledLabel = styled.label`
  display: block;
  grid-column: 1;
  grid-row: 1;
  margin: 0;
  ${pickThemeFontStyles('14', '20', 'NORMAL')};
  color: ${COLORS.BLACK};
`;

export const StyledTagField = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  box-sizing: border-box;
  min-height: 3rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${COLORS.SOFT_GRAY};
  border-radius: 0.375rem;
  background: ${COLORS.WHITE};
`;

export const StyledTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: ${COLORS.GRAY_LIGHT};
  color: ${COLORS.BLACK_PRIMARY};
  ${pickThemeFontStyles('14', '20', 'NORMAL')};
  border-radius: 0.25rem;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    min-height: 1.25rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${COLORS.BLACK_PRIMARY};
    cursor: pointer;
  }

  svg {
    width: 0.75rem;
    height: 0.75rem;
    fill: ${COLORS.BLACK_PRIMARY};
  }
`;

export const StyledTagInput = styled.input`
  flex: 1;
  min-width: 12rem;
  border: 0;
  outline: none;
  background: transparent;
  ${pickThemeFontStyles('14', '20', 'NORMAL')};
`;

export const StyledSuggestions = styled.ul`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 0.25rem);
  z-index: 20;
  max-height: 16rem;
  overflow-y: auto;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.SOFT_GRAY};
  border-radius: 0.375rem;
  box-shadow: 0 0.25rem 0.75rem rgb(0 0 0 / 8%);
`;

export const StyledSuggestionItem = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  ${pickThemeFontStyles('14', '20', 'NORMAL')};

  &:hover {
    background: ${COLORS.GRAY_LIGHT};
  }
`;

export const StyledSuggestionSku = styled.span`
  ${pickThemeFontStyles('14', '20', 'BOLD')};
  color: ${COLORS.BLACK_PRIMARY};
`;

export const StyledSuggestionMeta = styled.span`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

export const StyledFindButton = styled(Button)`
  &&& {
    grid-column: 2;
    grid-row: 2;
    align-self: stretch;
    height: auto;
    min-height: 3rem;
    background: ${COLORS.WHITE};
    border: 1px solid ${COLORS.YELLOW_PRIMARY};
    color: ${COLORS.BLACK_PRIMARY};

    &:hover {
      background: ${COLORS.YELLOW_PRIMARY};
      border-color: ${COLORS.YELLOW_PRIMARY};
      color: ${COLORS.BLACK_PRIMARY};
    }
  }
`;

export const StyledErrors = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  color: ${COLORS.RED};
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
`;

export const StyledColumns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(12rem, 0.9fr);
  gap: 1.25rem;
  min-height: 18rem;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid ${COLORS.LIGHT_GRAY};
  border-radius: 0.5rem;
  overflow: hidden;
`;

export const StyledColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${COLORS.LIGHT_GRAY};
`;

export const StyledColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${pickThemeFontStyles('16', '22', 'NORMAL')};
`;

export const StyledCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  border-radius: 99em;
  background: ${COLORS.BLACK_PRIMARY};
  color: ${COLORS.WHITE};
  ${pickThemeFontStyles('12', '16', 'NORMAL')};
`;

export const StyledSelectAll = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  ${pickThemeFontStyles('14', '20', 'NORMAL')};
`;

export const StyledColumnBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0 0.5rem 0.5rem;

  && .ant-table-wrapper {
    width: 100%;
  }

  && .ant-table-thead .ant-table-cell,
  && .ant-table-column-has-sorters {
    color: ${COLORS.BLACK_PRIMARY};
    background: ${COLORS.WHITE};
    border-bottom: 1px solid ${COLORS.LIGHT_GRAY};
    padding: 0.5rem 0.375rem;
    ${pickThemeFontStyles('12', '18', 'NORMAL')};

    &::before {
      display: none;
    }
  }

  && .ant-table-cell {
    min-width: 0;
    padding: 0.5rem 0.375rem;
    ${pickThemeFontStyles('12', '18', 'NORMAL')};
    word-break: break-word;
  }

  && .ant-table-tbody .ant-table-cell {
    vertical-align: middle;
  }
`;

export const StyledProductName = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledProductCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const StyledProductImage = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  object-fit: cover;
  border-radius: 0.25rem;
  background: ${COLORS.GRAY_LIGHT};
`;

export const StyledAddedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${COLORS.LIGHT_GRAY};
`;

export const StyledAddedInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const StyledAddedName = styled.span`
  ${pickThemeFontStyles('14', '20', 'NORMAL')};
  color: ${COLORS.BLACK_PRIMARY};
`;

export const StyledAddedSku = styled.span`
  ${pickThemeFontStyles('12', '18', 'NORMAL')};
  color: ${COLORS.GRAY_PRIMARY};
`;

export const StyledPaginationWrap = styled.div`
  padding: 0.75rem 1rem;
  border-top: 1px solid ${COLORS.LIGHT_GRAY};
`;

export const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

export const StyledCancelButton = styled(Button)`
  &&& {
    background: ${COLORS.WHITE};
    color: ${COLORS.BLACK_PRIMARY};
    border: 1px solid ${COLORS.SOFT_GRAY};
  }
`;

export const StyledAddButton = styled(Button)`
  &&& {
    background-color: ${COLORS.YELLOW_PRIMARY};
    border-color: transparent;
    color: ${COLORS.BLACK_PRIMARY};

    &:hover {
      background-color: ${COLORS.YELLOW_PRIMARY};
      color: ${COLORS.BLACK_PRIMARY};
    }

    &:disabled {
      opacity: 0.5;
    }
  }
`;

export const StyledSaveButton = styled(Button)`
  &&& {
    background: ${COLORS.WHITE};
    color: ${COLORS.BLACK_PRIMARY};
    border: 1px solid ${COLORS.YELLOW_PRIMARY};

    &:hover {
      background: ${COLORS.YELLOW_PRIMARY};
      border-color: ${COLORS.YELLOW_PRIMARY};
      color: ${COLORS.BLACK_PRIMARY};
    }
  }
`;
